import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { NorvaWordmark } from '../brand';
import {
  IconChevronDown,
  IconLanguages,
  IconLogOut,
  IconMonitor,
  IconMoon,
  IconPanelLeftClose,
  IconPanelLeftOpen,
  IconSun,
} from '../icons';
import { LANGUAGES, language, setLanguage, upper, type Language } from '../i18n';
import { useTranslate } from '../i18n/react';
import { allowed, type Session } from '../auth/session';
import { maySee, type NavGroup } from '../layout/navigation';
import { setTheme, useTheme, type Theme } from '../theme';
import { IconButton } from './Button';

const COLLAPSED_KEY = 'norva.nav.collapsed';
const CLOSED_GROUPS_KEY = 'norva.nav.closedGroups';

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    // Storage can be blocked. A menu that opens with every group expanded is not a failure.
    return fallback;
  }
}

function writeJson(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Remembering is a convenience, not a requirement.
  }
}

/** Two letters, upper-cased in the right language: "İsmail Şahin" must not become "IS". */
function initials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean).slice(0, 2);
  return upper(parts.map((part) => part.slice(0, 1)).join('')) || '?';
}

const THEMES: { value: Theme; icon: typeof IconSun; key: string }[] = [
  { value: 'system', icon: IconMonitor, key: 'theme.system' },
  { value: 'light', icon: IconSun, key: 'theme.light' },
  { value: 'dark', icon: IconMoon, key: 'theme.dark' },
];

/**
 * The dark rail that carries the brand, the menu and whoever is signed in.
 *
 * <p>There is no top bar. The logo, the language, the theme and the user sit here, which
 * leaves the content the full height of the window — and these screens are mostly dense
 * tables, so height is the thing in shortest supply.</p>
 */
export function Sidebar({
  groups,
  session: current,
  onSignOut,
}: {
  groups: NavGroup[];
  session: Session | null;
  onSignOut: () => void;
}) {
  const t = useTranslate();
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState(() => readJson(COLLAPSED_KEY, false));
  const [closedGroups, setClosedGroups] = useState<string[]>(() => readJson(CLOSED_GROUPS_KEY, []));

  useEffect(() => writeJson(COLLAPSED_KEY, collapsed), [collapsed]);
  useEffect(() => writeJson(CLOSED_GROUPS_KEY, closedGroups), [closedGroups]);

  // Hide what this user cannot use, then hide a group left with nothing in it. Convenience
  // only: the endpoints behind every one of these pages check the same permission.
  const visible = groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => maySee(item, (p) => allowed(current, p))),
    }))
    .filter((group) => group.items.length > 0);

  const next = LANGUAGES[(LANGUAGES.findIndex((l) => l.code === language()) + 1) % LANGUAGES.length];
  const activeTheme = useTheme();

  return (
    <aside className={collapsed ? 'nv-sidebar nv-sidebar--collapsed' : 'nv-sidebar'} aria-label={t('nav.mainMenu')}>
      <div className="nv-sidebar__brand">
        <NorvaWordmark className="nv-sidebar__logo" />
        <IconButton
          className="nv-sidebar__collapse"
          label={collapsed ? t('nav.expand') : t('nav.collapse')}
          icon={collapsed ? IconPanelLeftOpen : IconPanelLeftClose}
          iconSize={20}
          aria-expanded={!collapsed}
          onClick={() => setCollapsed((c) => !c)}
        />
      </div>

      <nav className="nv-sidebar__nav">
        {visible.map((group) => {
          // The group holding the current page stays open; closing it would hide where you are.
          const holdsActive = group.items.some((i) => pathname === i.to || pathname.startsWith(`${i.to}/`));
          const open = holdsActive || !closedGroups.includes(group.id);
          const listId = `nav-group-${group.id}`;

          return (
            <div key={group.id} className="nv-nav-group" data-collapsed={!open}>
              <button
                type="button"
                className="nv-nav-group__toggle"
                aria-expanded={open}
                aria-controls={listId}
                onClick={() =>
                  !holdsActive &&
                  setClosedGroups((prev) =>
                    prev.includes(group.id) ? prev.filter((x) => x !== group.id) : [...prev, group.id],
                  )
                }
              >
                {t(group.labelKey)}
                <IconChevronDown size={14} />
              </button>

              <ul className="nv-nav-list" id={listId}>
                {group.items.map(({ to, labelKey, icon: Icon }) => (
                  <li key={to}>
                    {/* NavLink sets aria-current="page"; the amber indicator is styled off that state. */}
                    <NavLink to={to} className="nv-nav-item" title={collapsed ? t(labelKey) : undefined}>
                      <Icon size={17} />
                      <span className="nv-nav-item__label">{t(labelKey)}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </nav>

      <div className="nv-sidebar__footer">
        <div className="nv-theme-row" role="group" aria-label={t('theme.label')}>
          {THEMES.map(({ value, icon: Icon, key }) => (
            <button
              key={value}
              type="button"
              className="nv-theme-btn"
              aria-pressed={activeTheme === value}
              title={t(key)}
              aria-label={t(key)}
              onClick={() => setTheme(value)}
            >
              <Icon size={15} />
            </button>
          ))}
        </div>

        <button
          type="button"
          className="nv-lang-row"
          onClick={() => next && setLanguage(next.code as Language)}
          title={collapsed ? t('app.language') : undefined}
          aria-label={`${t('app.language')}: ${LANGUAGES.find((l) => l.code === language())?.label ?? ''}`}
        >
          <IconLanguages size={17} />
          <span>{t('app.language')}</span>
          <span className="nv-lang-row__value">{LANGUAGES.find((l) => l.code === language())?.label}</span>
        </button>

        <div className="nv-user">
          <span className="nv-avatar" aria-hidden="true">{initials(current?.name ?? '')}</span>
          <div className="nv-user__meta">
            <div className="nv-user__name">{current?.name}</div>
            <div className="nv-user__mail">{current?.email}</div>
          </div>
          <IconButton label={t('app.signOut')} icon={IconLogOut} onClick={onSignOut} />
        </div>
      </div>
    </aside>
  );
}
